import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { NotificationsService } from '@app/components/notification/NotificationsService';
import { AccountDao } from '@app/dao/account/account.dao';
import { UserService } from '@app/providers/user/user.service';
import { passwordRuleErrors } from '@app/services/password-rules';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

@Component({
  selector: 'ea-modal-password',
  templateUrl: './modal-password.component.html',
  styleUrls: ['./modal-password.component.scss'],
  standalone: false,
})
export class ModalPasswordComponent implements OnInit {
  //TODO fix type
  @ViewChild('passwordModal', { static: true })
  passwordModalTemplate: any;

  @Input() simulation: boolean;

  @Output() closed = new EventEmitter<void>();

  form: UntypedFormGroup;
  passwordModalRef: BsModalRef;
  submitting: boolean = false;
  passwordInitKey: string;
  accountId: number;
  userAccountId: number;

  constructor(
    private modalService: BsModalService,
    private authentificationService: AuthentificationService,
    private notificationService: NotificationsService,
    private userService: UserService,
    private fb: UntypedFormBuilder,
    private accountDao: AccountDao,
  ) {}

  ngOnInit(): void {
    const autologinToken = this.authentificationService.autologinToken;

    if (autologinToken) {
      this.userService.getUser().subscribe(user => {
        if (!user.sessionConsultant) {
          this.accountId = user.accountId;
          this.userAccountId = user?.userAccount?.id;
          this.accountDao.getUserAccount().subscribe(
            res => {
              this.passwordInitKey = res.activationKey;
              this.initializeForm();
              this.openModal();
            },
            () => {
              console.error(
                'Cannot open password modal: failed to generate password init key',
              );
            },
          );
        } else {
          console.error(
            'Cannot open password modal: logged-in user is a consultant',
          );
        }
      });
    } else {
      console.error(
        'Cannot open password modal: no autologin token found in local storage',
      );
    }
  }

  private initializeForm() {
    this.form = this.fb.group(
      {
        password: [null, Validators.compose([Validators.required])],
        passwordconfirm: [null, Validators.compose([Validators.required])],
      },
      {
        validators: [
          this.passwordValidator('password'),
          this.passwordConfirmationValidator('password', 'passwordconfirm'),
        ],
        updateOn: 'submit',
      },
    );
  }

  private openModal() {
    this.passwordModalRef = this.modalService.show(this.passwordModalTemplate, {
      backdrop: true,
      class: 'password-modal',
      ignoreBackdropClick: true,
    });
  }

  passwordError(): boolean {
    return this.form.errors?.invalidPassword;
  }

  passwordConfirmationError(): boolean {
    return !this.passwordError() && this.form.errors?.confirmationMismatch;
  }

  onSubmit(): void {
    this.submitting = true;

    if (this.form.invalid) {
      this.form.setErrors({ ...this.form.errors, invalidForm: true });
      this.submitting = false;
      return;
    }

    this.userService
      .initializePassword(this.accountId, this.userAccountId, {
        password: this.form.get('password').value,
      })
      .subscribe(
        () => {
          this.notificationService.success(
            'Le mot de passe a été changé avec succès',
          );
          this.closeModal();
        },
        () => {
          this.notificationService.error('Une erreur est survenue');
          this.closeModal();
        },
      );
  }

  closeModal() {
    this.submitting = false;
    this.passwordModalRef?.hide();
    this.closed.emit();
  }

  private passwordValidator(passwordKey: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.get(passwordKey).value;
      if (!password) return null;

      const passwordErrors = passwordRuleErrors(password);

      if (passwordErrors.length > 0) {
        return { invalidPassword: true, passwordErrors };
      }

      return null;
    };
  }

  private passwordConfirmationValidator(
    passwordKey: string,
    passwordConfirmationKey: string,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.get(passwordKey).value;
      const passwordConfirmation = control.get(passwordConfirmationKey).value;
      if (passwordConfirmation !== password) {
        return { confirmationMismatch: true };
      }

      return null;
    };
  }
}
